import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = (token) => ({ Authorization: `Bearer ${token}` });

const TYPE_COLORS = {
  scholarship: "bg-blue-100 text-blue-800",
  waiver: "bg-purple-100 text-purple-800",
};

function ApproveRejectModal({ open, onClose, item, action, token, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (action === "reject" && !reason.trim()) {
      toast.error("Rejection reason required"); return;
    }
    setLoading(true);
    const baseUrl = item.item_type === "scholarship"
      ? `${API}/api/finance/fm4a/awards/${item.id}`
      : `${API}/api/finance/fm4a/waiver-requests/${item.id}`;
    const endpoint = `${baseUrl}/${action}`;
    try {
      await axios.patch(endpoint, { reason }, { headers: hdrs(token) });
      toast.success(`${item.item_type} ${action === "approve" ? "approved" : "rejected"}`);
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="approve-reject-modal">
        <DialogHeader>
          <DialogTitle>
            {action === "approve" ? "Approve" : "Reject"} — {item?.ref}
          </DialogTitle>
        </DialogHeader>
        <div className="py-3 space-y-3">
          <div className="text-sm text-muted-foreground">
            <span className="capitalize">{item?.item_type}</span> · {item?.label} · Amount: <strong>{item?.amount}</strong>
          </div>
          <div>
            <Label>{action === "reject" ? "Rejection reason *" : "Notes (optional)"}</Label>
            <Textarea
              data-testid="approval-reason-input"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder={action === "approve" ? "Optional notes…" : "Required reason for rejection…"}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="approval-action-submit"
            variant={action === "approve" ? "default" : "destructive"}
            onClick={submit}
            disabled={loading}
          >
            {loading ? "…" : action === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FM4ApprovalQueue() {
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // { item, action }
  const token = localStorage.getItem("token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType !== "all" ? `?item_type=${filterType}` : "";
      const r = await axios.get(`${API}/api/finance/fm4a/approval-queue${params}`, { headers: hdrs(token) });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load approval queue");
    } finally { setLoading(false); }
  }, [token, filterType]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="approval-queue-page">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">FM4A Approval Queue</h1>
          <p className="text-sm text-muted-foreground mt-1">Pending scholarship awards and waiver requests awaiting your approval</p>
        </div>
        <Button variant="outline" size="sm" data-testid="refresh-queue-btn" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40" data-testid="queue-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="scholarship">Scholarships</SelectItem>
            <SelectItem value="waiver">Waivers</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{items.length} item{items.length !== 1 ? "s" : ""} pending</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border rounded-lg" data-testid="empty-queue">
          <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-3" />
          <p className="text-sm font-medium">Queue is clear</p>
          <p className="text-xs text-muted-foreground mt-1">No pending approvals</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="approval-queue-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b text-left text-xs text-muted-foreground">
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Ref</th>
                <th className="py-3 px-4">Scheme / Type</th>
                <th className="py-3 px-4">Amount</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Submitted</th>
                <th className="py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={`${item.item_type}-${item.id}`} className="border-b hover:bg-muted/20" data-testid={`queue-item-${item.id}`}>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[item.item_type] || "bg-gray-100 text-gray-600"}`}>
                      {item.item_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs">{item.ref}</td>
                  <td className="py-3 px-4 max-w-[200px] truncate">{item.label}</td>
                  <td className="py-3 px-4">{item.amount}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">{item.status}</span>
                  </td>
                  <td className="py-3 px-4 text-xs text-muted-foreground">
                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`queue-approve-${item.id}`}
                        onClick={() => setModal({ item, action: "approve" })}
                        className="h-7 text-xs"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />Approve
                      </Button>
                      {item.item_type === "waiver" && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`queue-reject-${item.id}`}
                          onClick={() => setModal({ item, action: "reject" })}
                          className="h-7 text-xs text-red-600 hover:text-red-700"
                        >
                          <XCircle className="w-3 h-3 mr-1" />Reject
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ApproveRejectModal
          open={!!modal}
          onClose={() => setModal(null)}
          item={modal.item}
          action={modal.action}
          token={token}
          onDone={load}
        />
      )}
    </div>
  );
}
